import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    nombre_completo: '',
    email: '',
    telefono: '',
    pais: '',
    dedicacion_actual: '',
    experiencia_futbol: '',
    punto_actual_futbol: '',
    objetivo_agente360: '',
    por_que_mentoria: '',
    dispuesto_invertir: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Basic validation
    const requiredFields = Object.keys(formData);
    const emptyFields = requiredFields.filter(field => !formData[field].trim());
    
    if (emptyFields.length > 0) {
      setError('Por favor completa todos los campos');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/applications`, formData);
      
      if (response.data.success) {
        // Redirect to success page
        navigate('/aplicacion-exitosa');
      }
    } catch (err) {
      console.error('Error submitting application:', err);
      setError('Hubo un error al enviar tu aplicación. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black py-20">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container-custom py-4 flex justify-between items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/yf77589j_IMG_1273.png" 
            alt="Agente 360" 
            className="h-12 object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
          <Button 
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:text-amber-400"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        </div>
      </header>

      <div className="container-custom pt-20 max-w-3xl">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Completa tu aplicación para <span className="text-amber-400">Agente 360</span>
          </h1>
          <p className="text-xl text-gray-400">
            Revisaremos tu perfil y, si encajas, podrás agendar una llamada.
          </p>
        </div>

        {/* Form Card */}
        <Card className="p-8 bg-neutral-900 border-white/10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información Personal */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-white border-b border-amber-400 pb-2">
                Información Personal
              </h2>
              
              <div>
                <Label htmlFor="nombre_completo" className="text-gray-300">
                  Nombre completo *
                </Label>
                <Input
                  id="nombre_completo"
                  name="nombre_completo"
                  value={formData.nombre_completo}
                  onChange={handleChange}
                  placeholder="Tu nombre completo"
                  className="mt-1 bg-black border-white/10 text-white"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-gray-300">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    className="mt-1 bg-black border-white/10 text-white"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="telefono" className="text-gray-300">
                    Teléfono / WhatsApp *
                  </Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="+56 9 1234 5678"
                    className="mt-1 bg-black border-white/10 text-white"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="pais" className="text-gray-300">
                  País de residencia *
                </Label>
                <Input
                  id="pais"
                  name="pais"
                  value={formData.pais}
                  onChange={handleChange}
                  placeholder="Ej: Chile, España, México..."
                  className="mt-1 bg-black border-white/10 text-white"
                  required
                />
              </div>
            </div>

            {/* Situación Profesional */}
            <div className="space-y-4 pt-6">
              <h2 className="text-2xl font-bold text-white border-b border-amber-400 pb-2">
                Situación Profesional
              </h2>

              <div>
                <Label htmlFor="dedicacion_actual" className="text-gray-300">
                  ¿A qué te dedicas actualmente? *
                </Label>
                <Textarea
                  id="dedicacion_actual"
                  name="dedicacion_actual"
                  value={formData.dedicacion_actual}
                  onChange={handleChange}
                  placeholder="Cuéntanos a qué te dedicas profesionalmente hoy..."
                  className="mt-1 bg-black border-white/10 text-white min-h-[100px]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="experiencia_futbol" className="text-gray-300">
                  ¿Tienes experiencia previa en fútbol? *
                </Label>
                <Textarea
                  id="experiencia_futbol"
                  name="experiencia_futbol"
                  value={formData.experiencia_futbol}
                  onChange={handleChange}
                  placeholder="Jugador, entrenador, scout, ninguna experiencia... Cuéntanos tu historia"
                  className="mt-1 bg-black border-white/10 text-white min-h-[100px]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="punto_actual_futbol" className="text-gray-300">
                  ¿En qué punto estás hoy dentro del fútbol? *
                </Label>
                <Textarea
                  id="punto_actual_futbol"
                  name="punto_actual_futbol"
                  value={formData.punto_actual_futbol}
                  onChange={handleChange}
                  placeholder="Quiero entrar pero no sé cómo, ya trabajo con jugadores, estoy estancado..."
                  className="mt-1 bg-black border-white/10 text-white min-h-[100px]"
                  required
                />
              </div>
            </div>

            {/* Motivación */}
            <div className="space-y-4 pt-6">
              <h2 className="text-2xl font-bold text-white border-b border-amber-400 pb-2">
                Motivación y Objetivos
              </h2>

              <div>
                <Label htmlFor="objetivo_agente360" className="text-gray-300">
                  ¿Qué te gustaría conseguir entrando a Agente 360? *
                </Label>
                <Textarea
                  id="objetivo_agente360"
                  name="objetivo_agente360"
                  value={formData.objetivo_agente360}
                  onChange={handleChange}
                  placeholder="¿Qué esperas lograr con esta mentoría?"
                  className="mt-1 bg-black border-white/10 text-white min-h-[120px]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="por_que_mentoria" className="text-gray-300">
                  ¿Por qué crees que esta mentoría puede ayudarte? *
                </Label>
                <Textarea
                  id="por_que_mentoria"
                  name="por_que_mentoria"
                  value={formData.por_que_mentoria}
                  onChange={handleChange}
                  placeholder="¿Qué te hace pensar que Agente 360 es lo que necesitas?"
                  className="mt-1 bg-black border-white/10 text-white min-h-[120px]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="dispuesto_invertir" className="text-gray-300">
                  ¿Estás dispuesto a invertir en tu crecimiento profesional si ves que esta mentoría es para ti? *
                </Label>
                <Textarea
                  id="dispuesto_invertir"
                  name="dispuesto_invertir"
                  value={formData.dispuesto_invertir}
                  onChange={handleChange}
                  placeholder="Sé honesto. Esto es construcción de largo plazo."
                  className="mt-1 bg-black border-white/10 text-white min-h-[100px]"
                  required
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg">
                <p className="text-red-500 text-center">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              size="lg"
              className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-bold text-lg py-6 hover:from-amber-500 hover:to-yellow-600 transition-all duration-300"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Enviando aplicación...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-5 w-5" />
                  Enviar Aplicación
                </>
              )}
            </Button>
          </form>
        </Card>

        {/* Footer Note */}
        <p className="text-center text-gray-500 mt-8 text-sm">
          Al enviar tu aplicación, aceptas que revisaremos tu perfil y te contactaremos si encajas en Agente 360.
        </p>
      </div>
    </div>
  );
};

export default ApplicationForm;
